import React, { useContext, useState } from "react";
import styled from "styled-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal, Tag, Button, message, Tooltip, Empty, Card } from "antd";
import {
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ToolOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import RepairModel from "../lib/firebase/repairModel";
import PageHeader from "../components/PageHeader";
import { device } from "../lib/breakpoints";
import { getAuth } from "firebase/auth";
import { getApiUrl } from "../lib/api/config";
import {
  getWorkOrdersForTicket,
  updateWorkOrderStatus,
} from "../lib/api/vendorBidApi";
import { shouldShowScaleUpgradeForFullOs } from "../helpers/accountProduct";
import ScaleUpgradeModal from "../components/ScaleUpgradeModal";

const formatMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function platformFeeFromBid(bid) {
  const total = Number(bid.final_bid_amount ?? bid.bidAmount) || 0;
  const pct = bid.platform_fee_percent;
  if (typeof pct !== "number" || pct <= 0) return { total, fee: 0 };
  const fee = Math.round(total * (pct / 100) * 100) / 100;
  return { total, fee };
}

function acceptBidConfirmContent(bid) {
  const { total, fee } = platformFeeFromBid(bid);
  return (
    <div>
      <p>
        Are you sure you want to accept this bid from {bid.vendorName} for $
        {formatMoney(total)}? You can then Approve the work order to notify the
        contractor.
      </p>
      {fee > 0 && (
        <p style={{ marginTop: 12 }}>
          <strong>Total cost:</strong> ${formatMoney(total)} (Includes $
          {formatMoney(fee)} LandlordOps Service Fee paid by vendor)
        </p>
      )}
    </div>
  );
}

const BiddingDashboard = () => {
  const { user, userInfo } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [selectedBid, setSelectedBid] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [scaleUpgradeOpen, setScaleUpgradeOpen] = useState(false);

  const apiUrl = getApiUrl();

  // Fetch all bids for this landlord's repairs (vendor-bids API)
  const { data: bids = [], isLoading } = useQuery({
    queryKey: ["bids", user?.uid],
    queryFn: async () => {
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();
      const repairs = await RepairModel.getByOwner(user.uid);
      const allBids = [];
      for (const repair of repairs) {
        const response = await fetch(
          `${apiUrl}/vendor-bids/tickets/${repair.id}/bids`,
          {
            headers: { ...(token && { Authorization: `Bearer ${token}` }) },
          }
        );
        if (response.ok) {
          const data = await response.json();
          const list = data.bids || [];
          allBids.push(
            ...list.map((b) => ({ ...b, ticketId: b.ticketId || repair.id }))
          );
        }
      }
      return allBids;
    },
    enabled: !!user?.uid,
  });

  // Fetch repairs to show repair details
  const { data: repairs = [] } = useQuery({
    queryKey: ["repairs", user?.uid],
    queryFn: () => RepairModel.getByOwner(user.uid),
    enabled: !!user?.uid,
  });

  const getRepairDetails = (repairId) => {
    return repairs.find((r) => r.id === repairId);
  };

  // Work orders for selected accepted bid (for Approve/Deny)
  const { data: workOrdersData } = useQuery({
    queryKey: ["workOrders", selectedBid?.ticketId],
    queryFn: () => getWorkOrdersForTicket(selectedBid.ticketId),
    enabled: !!selectedBid?.ticketId && selectedBid?.status === "accepted",
  });
  const workOrders = workOrdersData?.workOrders || [];
  const pendingWorkOrder = workOrders.find(
    (wo) => wo.status === "pending_approval"
  );

  const handleApproveWorkOrder = async () => {
    if (!pendingWorkOrder || !user?.uid) return;
    try {
      setActionLoading(true);
      await updateWorkOrderStatus(pendingWorkOrder.id, "approved", {
        landlordId: user.uid,
      });
      message.success(
        "Work order approved. Contractor notified and you were CC'd."
      );
      queryClient.invalidateQueries({
        queryKey: ["workOrders", selectedBid?.ticketId],
      });
      queryClient.invalidateQueries({ queryKey: ["bids", user?.uid] });
      queryClient.invalidateQueries({ queryKey: ["repairs", user?.uid] });
      setIsModalVisible(false);
    } catch (e) {
      message.error(e.message || "Failed to approve work order");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDenyWorkOrder = async () => {
    if (!pendingWorkOrder || !user?.uid) return;
    Modal.confirm({
      title: "Deny Work Order",
      content:
        "Are you sure you want to deny this work order? The contractor will not be notified.",
      okText: "Deny",
      okType: "danger",
      onOk: async () => {
        try {
          setActionLoading(true);
          await updateWorkOrderStatus(pendingWorkOrder.id, "denied", {
            landlordId: user.uid,
          });
          message.success("Work order denied.");
          queryClient.invalidateQueries({
            queryKey: ["workOrders", selectedBid?.ticketId],
          });
          queryClient.invalidateQueries({ queryKey: ["bids", user?.uid] });
          queryClient.invalidateQueries({ queryKey: ["repairs", user?.uid] });
          setIsModalVisible(false);
        } catch (e) {
          message.error(e.message || "Failed to deny work order");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleViewBid = (bid) => {
    setSelectedBid(bid);
    setIsModalVisible(true);
  };

  const handleAcceptBid = async (bid) => {
    Modal.confirm({
      title: "Accept Bid",
      content: acceptBidConfirmContent(bid),
      okText: "Accept Bid",
      okType: "primary",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setActionLoading(true);
          const auth = getAuth();
          const token = await auth.currentUser?.getIdToken();
          const response = await fetch(
            `${apiUrl}/vendor-bids/bids/${bid.id}/status`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                status: "accepted",
                landlordId: user?.uid,
              }),
            }
          );
          if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || err.error || "Failed to accept bid");
          }
          message.success(
            "Bid accepted. Approve the work order to notify the contractor."
          );
          queryClient.invalidateQueries({ queryKey: ["bids", user?.uid] });
          queryClient.invalidateQueries({ queryKey: ["repairs", user?.uid] });
          queryClient.invalidateQueries({
            queryKey: ["workOrders", selectedBid?.ticketId],
          });
          setIsModalVisible(false);
        } catch (error) {
          console.error("Error accepting bid:", error);
          message.error(error.message || "Failed to accept bid");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleRejectBid = async (bid) => {
    Modal.confirm({
      title: "Reject Bid",
      content: `Are you sure you want to reject this bid from ${bid.vendorName}?`,
      okText: "Reject",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setActionLoading(true);
          const auth = getAuth();
          const token = await auth.currentUser?.getIdToken();
          const response = await fetch(
            `${apiUrl}/vendor-bids/bids/${bid.id}/status`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                status: "rejected",
                landlordId: user?.uid,
              }),
            }
          );
          if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || err.error || "Failed to reject bid");
          }
          message.success("Bid rejected");
          queryClient.invalidateQueries({ queryKey: ["bids", user?.uid] });
          setIsModalVisible(false);
        } catch (error) {
          console.error("Error rejecting bid:", error);
          message.error(error.message || "Failed to reject bid");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "accepted":
        return "success";
      case "rejected":
        return "error";
      case "withdrawn":
        return "default";
      case "pending":
        return "processing";
      default:
        return "default";
    }
  };

  const pendingBids = bids.filter((b) => b.status === "pending");
  const respondedBids = bids.filter((b) => b.status !== "pending");

  const needsScaleGate = shouldShowScaleUpgradeForFullOs(userInfo);

  if (needsScaleGate) {
    return (
      <Container>
        <PageHeader
          title="Vendor bidding"
          subtitle="Need more room to grow?"
          icon={DollarOutlined}
        />
        <Card style={{ maxWidth: 560, margin: "2rem auto" }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                Need more room to grow? Upgrade your door cap via Billing to
                manage more units seamlessly with your full operational
                workspace.
              </span>
            }
          >
            <Button
              type="primary"
              icon={<LockOutlined />}
              onClick={() => setScaleUpgradeOpen(true)}
            >
              View billing and upgrade
            </Button>
          </Empty>
        </Card>
        <ScaleUpgradeModal
          open={scaleUpgradeOpen}
          onClose={() => setScaleUpgradeOpen(false)}
        />
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <PageHeader
          title="Contractor Bids"
          subtitle="Review and manage bids from contractors"
          icon={DollarOutlined}
        />
        <LoadingText>Loading bids...</LoadingText>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title="Contractor Bids"
        subtitle="Review and manage bids from contractors"
        icon={DollarOutlined}
      />

      {/* Stats */}
      <StatsRow>
        <StatCard>
          <StatIcon>
            <ClockCircleOutlined />
          </StatIcon>
          <StatValue>{pendingBids.length}</StatValue>
          <StatLabel>Pending Bids</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon>
            <CheckCircleOutlined />
          </StatIcon>
          <StatValue>
            {bids.filter((b) => b.status === "accepted").length}
          </StatValue>
          <StatLabel>Accepted</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon>
            <CloseCircleOutlined />
          </StatIcon>
          <StatValue>
            {bids.filter((b) => b.status === "rejected").length}
          </StatValue>
          <StatLabel>Rejected</StatLabel>
        </StatCard>
      </StatsRow>

      {/* Pending Bids */}
      {pendingBids.length > 0 && (
        <Section>
          <SectionTitle>Pending Bids</SectionTitle>
          <BidsGrid>
            {pendingBids.map((bid) => {
              const repair = getRepairDetails(bid.ticketId);
              return (
                <BidCard key={bid.id} onClick={() => handleViewBid(bid)}>
                  <BidHeader>
                    <ContractorName>{bid.vendorName}</ContractorName>
                    <Tag color={getStatusColor(bid.status)}>{bid.status}</Tag>
                  </BidHeader>
                  <RepairTitle>
                    <ToolOutlined /> {repair?.title || "Repair"}
                  </RepairTitle>
                  <BidAmount>${bid.bidAmount?.toLocaleString()}</BidAmount>
                  <BidInfo>
                    <InfoItem>
                      <CalendarOutlined />{" "}
                      {bid.timeline || "Timeline not specified"}
                    </InfoItem>
                    <InfoItem>
                      <ClockCircleOutlined />{" "}
                      {new Date(
                        bid.createdAt || bid.submittedAt
                      ).toLocaleDateString()}
                    </InfoItem>
                  </BidInfo>
                </BidCard>
              );
            })}
          </BidsGrid>
        </Section>
      )}

      {/* Previous Bids */}
      {respondedBids.length > 0 && (
        <Section>
          <SectionTitle>Previous Bids</SectionTitle>
          <BidsGrid>
            {respondedBids.map((bid) => {
              const repair = getRepairDetails(bid.ticketId);
              return (
                <BidCard
                  key={bid.id}
                  onClick={() => handleViewBid(bid)}
                  $responded
                >
                  <BidHeader>
                    <ContractorName>{bid.vendorName}</ContractorName>
                    <Tag color={getStatusColor(bid.status)}>{bid.status}</Tag>
                  </BidHeader>
                  <RepairTitle>
                    <ToolOutlined /> {repair?.title || "Repair"}
                  </RepairTitle>
                  <BidAmount>${bid.bidAmount?.toLocaleString()}</BidAmount>
                  <BidInfo>
                    <InfoItem>
                      <CalendarOutlined />{" "}
                      {bid.timeline || "Timeline not specified"}
                    </InfoItem>
                    <InfoItem>
                      <ClockCircleOutlined />{" "}
                      {new Date(
                        bid.createdAt || bid.submittedAt
                      ).toLocaleDateString()}
                    </InfoItem>
                  </BidInfo>
                </BidCard>
              );
            })}
          </BidsGrid>
        </Section>
      )}

      {bids.length === 0 && (
        <EmptyState>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No bids yet. Create a repair ticket and request bids from contractors to get started."
          />
        </EmptyState>
      )}

      {/* Bid Detail Modal */}
      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        title="Bid Details"
        width={700}
        footer={
          selectedBid?.status === "pending"
            ? [
                <Button
                  key="reject"
                  danger
                  onClick={() => handleRejectBid(selectedBid)}
                  loading={actionLoading}
                >
                  Reject Bid
                </Button>,
                <Button
                  key="accept"
                  type="primary"
                  onClick={() => handleAcceptBid(selectedBid)}
                  loading={actionLoading}
                >
                  Accept Bid
                </Button>,
              ]
            : pendingWorkOrder
            ? [
                <Button
                  key="deny"
                  danger
                  onClick={handleDenyWorkOrder}
                  loading={actionLoading}
                >
                  Deny Work Order
                </Button>,
                <Button
                  key="approve"
                  type="primary"
                  onClick={handleApproveWorkOrder}
                  loading={actionLoading}
                >
                  Approve Work Order
                </Button>,
              ]
            : null
        }
      >
        {selectedBid && (
          <ModalContent>
            <ModalSection>
              <ModalSectionTitle>Vendor Information</ModalSectionTitle>
              <InfoRow>
                <strong>Name:</strong> {selectedBid.vendorName}
              </InfoRow>
              <InfoRow>
                <PhoneOutlined /> {selectedBid.phone || "Not provided"}
              </InfoRow>
              <InfoRow>
                <MailOutlined /> {selectedBid.vendorEmail || "Not provided"}
              </InfoRow>
            </ModalSection>

            <ModalSection>
              <ModalSectionTitle>Bid Details</ModalSectionTitle>
              <InfoRow>
                <strong>Amount:</strong>{" "}
                <BidAmountLarge>
                  $
                  {formatMoney(
                    selectedBid.final_bid_amount ?? selectedBid.bidAmount
                  )}
                </BidAmountLarge>
              </InfoRow>
              {(() => {
                const { total, fee } = platformFeeFromBid(selectedBid);
                if (fee <= 0) return null;
                return (
                  <InfoRow>
                    <strong>Total cost:</strong> ${formatMoney(total)}{" "}
                    <span style={{ color: "var(--color-text-muted)" }}>
                      (Includes ${formatMoney(fee)} LandlordOps Service Fee paid
                      by vendor)
                    </span>
                  </InfoRow>
                );
              })()}
              {selectedBid.estimatedHours && (
                <InfoRow>
                  <strong>Estimated Hours:</strong> {selectedBid.estimatedHours}{" "}
                  hours
                </InfoRow>
              )}
              {selectedBid.timeline && (
                <InfoRow>
                  <strong>Timeline:</strong> {selectedBid.timeline}
                </InfoRow>
              )}
              <InfoRow>
                <strong>Submitted:</strong>{" "}
                {new Date(
                  selectedBid.createdAt || selectedBid.submittedAt
                ).toLocaleString()}
              </InfoRow>
            </ModalSection>

            {selectedBid.notes && (
              <ModalSection>
                <ModalSectionTitle>Additional Notes</ModalSectionTitle>
                <Description>{selectedBid.notes}</Description>
              </ModalSection>
            )}

            {selectedBid.status !== "pending" && (
              <ModalSection>
                <Tag
                  color={getStatusColor(selectedBid.status)}
                  style={{ fontSize: "1rem", padding: "0.5rem 1rem" }}
                >
                  {selectedBid.status.toUpperCase()}
                </Tag>
                {selectedBid.respondedAt && (
                  <InfoRow style={{ marginTop: "0.5rem" }}>
                    Responded:{" "}
                    {new Date(selectedBid.respondedAt).toLocaleString()}
                  </InfoRow>
                )}
              </ModalSection>
            )}
          </ModalContent>
        )}
      </Modal>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;

  @media ${device.mobile} {
    padding: 1rem;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--color-text-muted);
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StatIcon = styled.div`
  font-size: 2rem;
  color: var(--color-primary);
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.95rem;
  color: var(--color-text-muted);
  font-weight: 500;
`;

const Section = styled.div`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 1.5rem;
  font-family: var(--font-primary);
`;

const BidsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

const BidCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.$responded ? 0.7 : 1)};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
`;

const BidHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ContractorName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text);
`;

const RepairTitle = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-muted);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BidAmount = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 0.75rem;
`;

const BidInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoItem = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EmptyState = styled.div`
  background: white;
  border-radius: 12px;
  padding: 4rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ModalSection = styled.div`
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const ModalSectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.75rem;
`;

const InfoRow = styled.div`
  font-size: 0.95rem;
  color: var(--color-text-muted);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  strong {
    color: var(--color-text);
    min-width: 140px;
  }
`;

const BidAmountLarge = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-primary);
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-muted);
  line-height: 1.6;
  white-space: pre-wrap;
`;

export default BiddingDashboard;
